import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';
import { FileText, Image as ImageIcon, Loader2, Paperclip, Send, X } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import {
  MAX_TICKET_ATTACHMENT_MB,
  MAX_TICKET_ATTACHMENTS,
  TICKET_ATTACHMENT_LABEL,
  TICKET_ATTACHMENT_TYPES,
} from '@/constants/support';
import { ApiRequestError } from '@/lib/apiClient';
import { formatFileSize } from '@/lib/format';

export interface TicketReplyBoxProps {
  /** Resolves once the server has the reply; rejects to keep the draft. */
  onSend: (message: string, files: File[]) => Promise<void>;
  /** Extra controls beside the Send button — our panel's "internal note" box. */
  extra?: ReactNode;
  sendLabel?: string;
  placeholder?: string;
}

const MAX_LENGTH = 5000;

/**
 * Writing on a ticket, with screenshots in a single-row chat input bar.
 */
export function TicketReplyBox({
  onSend,
  extra,
  sendLabel = 'Send reply',
  placeholder = 'Write a reply to our support team…',
}: TicketReplyBoxProps) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
    }
  }, [message]);

  function handleFileSelect(event: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = '';

    const accepted: File[] = [];
    let reason: string | null = null;
    let running = files.reduce((sum, file) => sum + file.size, 0);

    for (const file of picked) {
      if (files.length + accepted.length >= MAX_TICKET_ATTACHMENTS) {
        reason = `You can attach up to ${MAX_TICKET_ATTACHMENTS} files`;
        break;
      }
      if (!TICKET_ATTACHMENT_TYPES.includes(file.type as (typeof TICKET_ATTACHMENT_TYPES)[number])) {
        reason = `${file.name} is not a ${TICKET_ATTACHMENT_LABEL} file`;
        continue;
      }
      if (file.size > MAX_TICKET_ATTACHMENT_MB * 1024 * 1024) {
        reason = `${file.name} is larger than ${MAX_TICKET_ATTACHMENT_MB} MB`;
        continue;
      }
      running += file.size;
      accepted.push(file);
    }

    if (reason) setError(reason);
    else setError(null);
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function send() {
    const trimmed = message.trim();
    if (!trimmed && files.length === 0) {
      setError('Write a message first');
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      await onSend(trimmed, files);
      setMessage('');
      setFiles([]);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Your reply was not sent. Please try again.');
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift + Enter: Allow natural newline break
        return;
      }
      // Enter without Shift: Send message
      event.preventDefault();
      void send();
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <Alert tone="critical" title="Not sent">
          {error}
        </Alert>
      )}

      {extra && <div className="text-xs text-ink-3 px-1">{extra}</div>}

      <div className="rounded-2xl border-2 border-accent bg-surface shadow-xs transition-all hover:border-accent-2 focus-within:border-accent-2 focus-within:ring-2 focus-within:ring-accent/20 px-2.5 py-1.5 flex flex-col gap-1.5">
        {/* Attached files preview chips */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1 pt-0.5">
            {files.map((file, index) => (
              <span
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-0.5 text-xs text-ink-2 shadow-2xs"
              >
                {file.type === 'application/pdf' ? (
                  <FileText className="h-3 w-3 text-red-500 shrink-0" aria-hidden />
                ) : (
                  <ImageIcon className="h-3 w-3 text-blue-500 shrink-0" aria-hidden />
                )}
                <span className="max-w-[140px] truncate text-xs font-medium">{file.name}</span>
                <span className="text-[10px] text-ink-3">({formatFileSize(file.size)})</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={isSending}
                  aria-label={`Remove ${file.name}`}
                  className="rounded-full p-0.5 text-ink-3 hover:text-crit hover:bg-surface transition-colors cursor-pointer"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Single Row: [📎 Icon] [Textarea field] [Send Icon] */}
        <div className="flex items-end gap-2 w-full">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TICKET_ATTACHMENT_TYPES.join(',')}
            onChange={handleFileSelect}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
          />

          {/* Attachment Icon at starting */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending || files.length >= MAX_TICKET_ATTACHMENTS}
            title={`Attach files (${TICKET_ATTACHMENT_LABEL})`}
            aria-label="Attach files"
            className="grid h-8 w-8 shrink-0 mb-0.5 place-items-center rounded-full text-ink-3 hover:text-accent hover:bg-surface-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Paperclip className="h-4 w-4" aria-hidden />
          </button>

          {/* Input field with multiline & Shift+Enter support */}
          <textarea
            ref={textareaRef}
            rows={1}
            placeholder={placeholder}
            value={message}
            maxLength={MAX_LENGTH}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending}
            aria-label="Reply message"
            className="min-w-0 flex-1 bg-transparent px-2 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:outline-none resize-none max-h-36 overflow-y-auto leading-relaxed disabled:cursor-not-allowed"
          />

          {/* Send Icon Button */}
          <button
            type="button"
            onClick={() => void send()}
            disabled={isSending || (!message.trim() && files.length === 0)}
            title={sendLabel}
            aria-label={sendLabel}
            className="grid h-8 w-8 shrink-0 mb-0.5 place-items-center rounded-full bg-accent text-white shadow-xs hover:brightness-110 active:scale-95 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4 stroke-[2.2]" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
